import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Lock, ArrowLeft, ArrowRight, ShieldCheck, Loader2, Eye, EyeOff, CheckCircle2, Sun, Moon, Check, AlertTriangle, Home, Clock, Info } from "lucide-react";
import { Button } from "../ui/button";
import AuthFooter from "../Footer/AuthFooter"; 

export default function ResetPassword() {
  // Read the query string instead of path parameters
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token"); 
  const navigate = useNavigate();
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [apiSuccess, setApiSuccess] = useState("");
  const [apiError, setApiError] = useState("");
  
  const [fatalError, setFatalError] = useState(null); 
  const [countdown, setCountdown] = useState(5);

  const [theme, setTheme] = useState(
    typeof window !== "undefined" ? localStorage.getItem("theme") || "light" : "light"
  );

  // 1. TAMPER DETECTION
  useEffect(() => {
    // Check if token exists and matches standard Django token format
    const isTokenValid = token && /^[A-Za-z0-9_-]+-[A-Za-z0-9_-]+$/.test(token);
    
    if (!isTokenValid || token === ":token") {
      setFatalError({
        type: "invalid",
        title: "Invalid Reset Link",
        message: "This link is incomplete, tampered with, or invalid. Please request a new password reset."
      });
    }
  }, [token]);

  // 2. REAL-TIME COUNTDOWN
  useEffect(() => {
    let timer;
    if (fatalError) {
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate("/");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [fatalError, navigate]);

  useEffect(() => {
    const root = window.document.documentElement;
    theme === "dark" ? root.classList.add("dark") : root.classList.remove("dark");
    localStorage.setItem("theme", theme); 
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "light" ? "dark" : "light");

  const validations = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
  };

  const isPasswordValid = validations.length && validations.uppercase && validations.lowercase && validations.number;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setApiSuccess("");

    if (!isPasswordValid) {
      setApiError("Please ensure your password meets all the security requirements.");
      return;
    }
    if (password !== confirmPassword) {
      setApiError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      const cleanApiUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;

      // Send only the token and new_password
      const response = await fetch(`${cleanApiUrl}/api/password-reset/confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: password }),
      });

      let data;
      try {
        data = await response.json();
      } catch {
        data = { message: "Server endpoint not found." };
      }

      if (response.ok) {
        setApiSuccess("Password has been reset successfully! Redirecting to login...");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        const msg = (data.message || "").toLowerCase();
        if (msg.includes("used")) {
          setFatalError({
            type: "used",
            title: "Link Already Used",
            message: "You have already successfully changed your password using this link."
          });
        } else if (msg.includes("expired")) {
          setFatalError({
            type: "expired",
            title: "Link Expired",
            message: "This reset link has expired. Links are strictly valid for 5 minutes for your security."
          });
        } else {
          setFatalError({
            type: "invalid",
            title: "Invalid Link",
            message: "This password reset link is invalid or has been tampered with."
          });
        }
      }
    } catch (err) {
      setApiError("Network error. Please make sure the server is running.");
    } finally {
      setIsLoading(false);
    }
  };

  const ValidationItem = ({ isValid, text }) => (
    <div className={`flex items-center gap-2 text-xs transition-colors duration-300 ${isValid ? 'text-emerald-500' : 'text-slate-500 dark:text-slate-400'}`}>
      <Check className={`h-3.5 w-3.5 ${isValid ? 'opacity-100' : 'opacity-50'}`} strokeWidth={isValid ? 3 : 2} />
      <span>{text}</span>
    </div>
  );

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-slate-50 transition-colors duration-500 dark:bg-[#0d1f3c]">
      <Link 
        to="/" 
        className="fixed left-6 top-6 z-50 flex items-center gap-2 rounded-full bg-white/50 px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm backdrop-blur-md ring-1 ring-slate-200 transition-all hover:bg-white dark:bg-[#1a3358]/60 dark:text-slate-300 dark:ring-white/10 dark:hover:bg-[#1a3358]"
      >
        <ArrowLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Back to Home</span>
      </Link>

      <button onClick={toggleTheme} className="fixed right-6 top-6 z-50 rounded-full bg-white/50 p-3 text-slate-700 shadow-sm backdrop-blur-md ring-1 ring-slate-200 transition-all hover:bg-white dark:bg-[#1a3358]/60 dark:text-slate-300 dark:ring-white/10 dark:hover:bg-[#1a3358]">
        {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
      </button>

      <div className="pointer-events-none fixed inset-0 dark:bg-[radial-gradient(ellipse_at_top_right,rgba(251,191,36,0.10),transparent_50%)]" />

      <main className="relative z-10 flex flex-1 items-center justify-center px-4 py-12 transition-all duration-300">
        <div className="w-full max-w-md">
          {fatalError ? (
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-2xl backdrop-blur-xl dark:border-white/10 dark:bg-[#142a4d]/95 animate-in zoom-in-95 duration-500 text-center">
              <div className={`mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full ring-8 ${
                fatalError.type === 'expired' ? 'bg-amber-50 ring-amber-50/50 dark:bg-amber-500/10 dark:ring-amber-500/5' :
                fatalError.type === 'used' ? 'bg-blue-50 ring-blue-50/50 dark:bg-blue-500/10 dark:ring-blue-500/5' :
                'bg-red-50 ring-red-50/50 dark:bg-red-500/10 dark:ring-red-500/5'
              }`}>
                {fatalError.type === 'expired' && <Clock className="h-10 w-10 text-amber-500" />}
                {fatalError.type === 'used' && <Info className="h-10 w-10 text-blue-500" />}
                {fatalError.type === 'invalid' && <AlertTriangle className="h-10 w-10 text-red-500" />}
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-3">
                {fatalError.title}
              </h2>
              <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
                {fatalError.message}
              </p>
              <Button onClick={() => navigate("/")} className="w-full py-6 font-bold shadow-lg shadow-slate-200 dark:shadow-none bg-amber-500 hover:bg-amber-400 text-[#0d1f3c] transition-colors">
                <Home className="mr-2 h-4 w-4" /> Return to Home Now
              </Button>
              <div className="mt-6 flex items-center justify-center gap-2 text-xs font-semibold text-slate-400 dark:text-slate-500 animate-pulse">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Redirecting automatically in {countdown}s...
              </div>
            </div>
          ) : (
            <div className="animate-in fade-in duration-500">
              <div className="mb-8 flex flex-col items-center text-center">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/50 dark:bg-emerald-500/10 dark:ring-emerald-500/5">
                  <ShieldCheck className="h-10 w-10 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Set New Password</h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  Your new password must be strong and different from previously used passwords.
                </p>
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white/80 p-8 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-[#142a4d]/95">
                {apiSuccess ? (
                  <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 text-center">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
                      <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Password Reset!</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">{apiSuccess}</p>
                  </div>
                ) : (
                  <>
                    {apiError && (
                      <div className="mb-6 rounded-xl bg-red-50 p-4 border border-red-200 dark:bg-red-500/10 dark:border-red-500/20 animate-in fade-in duration-300">
                        <p className="text-sm text-red-600 dark:text-red-400 text-center font-medium">{apiError}</p>
                      </div>
                    )}

                    <form className="space-y-5" onSubmit={handleSubmit}>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">New Password</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                          <input 
                            type={showPassword ? "text" : "password"} 
                            value={password}
                            onFocus={() => setIsPasswordFocused(true)} 
                            onBlur={() => setIsPasswordFocused(false)}
                            onChange={(e) => { setPassword(e.target.value); setApiError(""); }}
                            placeholder="••••••••" 
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-10 text-sm text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-white/10 dark:bg-[#1a3358]/60 dark:text-white transition-colors" 
                          />
                          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                        
                        {(isPasswordFocused || password.length > 0) && (
                          <div className="mt-3 space-y-1.5 pb-2 animate-in fade-in duration-300">
                            <ValidationItem isValid={validations.length} text="At least 8 characters" />
                            <ValidationItem isValid={validations.uppercase} text="One uppercase letter" />
                            <ValidationItem isValid={validations.lowercase} text="One lowercase letter" />
                            <ValidationItem isValid={validations.number} text="One number" />
                          </div>
                        )}
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">Confirm Password</label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-3 h-5 w-5 text-slate-400" />
                          <input 
                            type={showPassword ? "text" : "password"} 
                            value={confirmPassword}
                            onChange={(e) => { setConfirmPassword(e.target.value); setApiError(""); }}
                            placeholder="••••••••" 
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-10 text-sm text-slate-900 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400 dark:border-white/10 dark:bg-[#1a3358]/60 dark:text-white transition-colors" 
                          />
                        </div>
                      </div>

                      <Button type="submit" disabled={isLoading || !isPasswordValid || !confirmPassword} className="mt-6 w-full py-6 text-base font-bold shadow-lg shadow-cyan-500/20 bg-amber-500 hover:bg-amber-400 text-[#0d1f3c] font-bold shadow-lg shadow-amber-500/25 disabled:opacity-60 disabled:cursor-not-allowed">
                        {isLoading ? (
                          <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Resetting...</>
                        ) : (
                          <>Reset Password <ArrowRight className="ml-2 h-4 w-4" /></>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      
      <AuthFooter />
    </div>
  );
}